import { useEffect, useState } from 'react'

import { Form, Input, Select, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'

import CommonModal from '@/components/common/CommonModal'
import Info from '@/components/common/Info'

import { SavedPicks, getSavedPicks, missingPickProps, missingPlaceholder } from './savedPicks'
import { ActionProps, ActionTypes, PointChangeAction } from './types'

export type PointChangeForm = {
  axis: 0 | 1
  control: number
  comparator: number
  output_name: string
}

const DEFAULT_OUTPUT_NAME = '% Point Change'

// Point change is materialized into the base table (issue #710), so it operates on the whole
// table's rows/columns just like % Uplift - `columns`/`rows` are the base labels.
const PointChange = ({
  columns = [],
  rows = [],
  onSubmit,
  onCancel,
  isVisible,
  initialValues,
  clearOptions = false
}: ActionProps<PointChangeAction>) => {
  // The edit payload carries ephemeral `control`/`comparator`/`by` fields not on the action
  // type, so read them loosely (as the other action modals do).
  const iv = (initialValues || {}) as any
  const saved = getSavedPicks(iv)
  const initial = {
    axis: iv.axis ?? 1,
    control: saved.control,
    comparator: saved.comparator,
    output_name: iv.output_name || DEFAULT_OUTPUT_NAME
  }
  const [form] = useForm<PointChangeForm>()
  const [control, setControl] = useState<number>(initial.control)
  const [comparator, setComparator] = useState<number>(initial.comparator)
  const [outputName, setOutputName] = useState<string>(initial.output_name)
  // Names of saved picks that no longer exist; shown until the user picks a replacement.
  const [missing, setMissing] = useState<SavedPicks>(saved)

  useEffect(() => {
    if (clearOptions === true) {
      form.resetFields()
      form.setFieldsValue(initial)
      setControl(initial.control)
      setComparator(initial.comparator)
      setOutputName(initial.output_name)
      setMissing(saved)
    }
  }, [clearOptions, initialValues])

  useEffect(() => {
    form.resetFields()
  }, [])

  const submitForm = () => {
    form.submit()
    const { axis, control, comparator, output_name } = form.getFieldsValue(true)
    onSubmit?.({
      actionType: ActionTypes.POINT_CHANGE,
      type: ActionTypes.POINT_CHANGE,
      axis,
      by: [control, comparator],
      specs: { output_name: output_name.trim() }
    })
  }

  const onAxisChange = (value: 0 | 1) => {
    form.setFieldsValue({ axis: value, control: undefined, comparator: undefined })
    setControl(undefined)
    setComparator(undefined)
    setMissing({})
  }

  const popoverContent = (
    <div>
      <strong>Function</strong>
      <p>
        Output the percentage-point change (comparator minus control) as a new column or row,
        shown to zero decimal places.
      </p>
      <strong>How to use</strong>
      <p>[Orientation]: Select if you want to compare columns or rows.</p>
      <p>[Control]: Select the baseline column or row.</p>
      <p>[Comparator]: Select the column or row to measure against the control.</p>
      <p>[Name output]: Enter a name for the new column or row (max 250 characters).</p>
    </div>
  )

  const canSubmit =
    control != null && comparator != null && control !== comparator && !!outputName?.trim()

  return (
    <CommonModal
      visible={isVisible}
      onCancel={onCancel}
      onOk={submitForm}
      okDisabled={!canSubmit}
      title={
        <span>
          % Point Change <Info>{popoverContent}</Info>
        </span>
      }
    >
      <p style={{ textAlign: 'center' }}>Comparator &minus; Control</p>
      <Form form={form} initialValues={initial} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item name="axis" rules={[{ required: true }]} label="Orientation">
            <Select onChange={onAxisChange}>
              <Select.Option value={1}>Columns</Select.Option>
              <Select.Option value={0}>Rows</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) => prev.axis !== cur.axis || prev.comparator !== cur.comparator}
          >
            {({ getFieldValue }) => {
              const axis = getFieldValue('axis')
              const options = (axis === 0 ? rows : columns).filter(
                ({ index }) => index !== getFieldValue('comparator')
              )
              return (
                <Form.Item
                  name="control"
                  label="Control"
                  rules={[{ required: true }]}
                  {...missingPickProps(missing.missingControl, control, 'control')}
                >
                  <Select
                    onChange={setControl}
                    placeholder={missingPlaceholder(missing.missingControl, control, 'control')}
                  >
                    {options.map(({ index, name }) => (
                      <Select.Option value={index} key={index}>
                        {name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )
            }}
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) => prev.axis !== cur.axis || prev.control !== cur.control}
          >
            {({ getFieldValue }) => {
              const axis = getFieldValue('axis')
              const options = (axis === 0 ? rows : columns).filter(
                ({ index }) => index !== getFieldValue('control')
              )
              return (
                <Form.Item
                  name="comparator"
                  label="Comparator"
                  rules={[{ required: true }]}
                  {...missingPickProps(missing.missingComparator, comparator, 'comparator')}
                >
                  <Select
                    onChange={setComparator}
                    placeholder={missingPlaceholder(
                      missing.missingComparator,
                      comparator,
                      'comparator'
                    )}
                  >
                    {options.map(({ index, name }) => (
                      <Select.Option value={index} key={index}>
                        {name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )
            }}
          </Form.Item>

          <Form.Item
            name="output_name"
            label="Name output"
            rules={[
              { required: true, whitespace: true, message: 'Please enter a name for the output.' },
              { max: 250, message: 'Name must be at most 250 characters.' }
            ]}
          >
            <Input
              maxLength={250}
              placeholder="Enter output name"
              onChange={e => setOutputName(e.target.value)}
            />
          </Form.Item>
        </Space>
      </Form>
    </CommonModal>
  )
}

export default PointChange
